// THE FRONTIER BAND: the world is placed as it is explored (Matt, after
// playtest 12: "we don't need raiders to make camps on the far end of the
// map that we're not going to see for 30 turns... generate like a couple of
// rings beyond where we can see, and then as we expand, we just generate the
// next level out").
//
// The ground itself is a pure function (map.js). What this file places on it
// - the region sites past the first ring, the finds - is placed only in the
// BAND: the unseen hexes within map.BAND of anything seen. `extendWorld` is
// idempotent: run it after any reveal and it adds what the band now reaches,
// never twice and never anything already taken. Camps are spawned only in
// the band too (`campSpots`): threats come from the edge of the known.
import {
  key, unkey, hexDistance, within, DIRS, add, hash, isLandHex, siteAt, isTown, BAND, NEAR_SITES,
} from './map';

const FAR_SITE_MIN = 6; // no far site nearer the town than this
const SITE_SPACING = 4; // nor nearer another site's middle
const SITE_CHANCE = 0.06; // a band hex that could hold a site, does, this often
const FIND_CHANCE = 0.035;
export const FIND_ORDER_KEYS = ['ruins', 'grove', 'wreck', 'lode'];

/** The unseen hexes within BAND of the seen. */
export const bandOf = (civ) => {
  const seen = new Set(civ.seen || []);
  const band = new Set();
  for (const k of seen) {
    const [q, r] = unkey(k);
    within(q, r, BAND).forEach(([a, b]) => { const kk = key(a, b); if (!seen.has(kk)) band.add(kk); });
  }
  return [...band].map(unkey);
};

const seedOf = (civ) => civ.mapSeed || 0;

/**
 * Add what the band now reaches: far region sites (while far regions are
 * left to deal, from `civ.farDeck`) and finds. Returns the civ, unchanged
 * when there is nothing new. Old saves (no `seen`) are left alone.
 */
export const extendWorld = (civ) => {
  if (!civ.seen) return civ;
  const seed = seedOf(civ);
  const band = bandOf(civ).sort((a, b) => hexDistance(...a) - hexDistance(...b) || hash(a[0], a[1], seed + 5) - hash(b[0], b[1], seed + 5));
  let farSites = civ.farSites || [];
  let farDeck = civ.farDeck || [];
  const regionSites = { ...(civ.regionSites || {}) };
  let finds = civ.finds || [];
  const taken = new Set(civ.findsTaken || []);
  let changed = false;

  for (const [q, r] of band) {
    const d = hexDistance(q, r);
    // A region site: three hexes of land, well out, room around it.
    if (farDeck.length && d >= FAR_SITE_MIN && hash(q, r, seed + 5) < SITE_CHANCE && siteAt(q, r, { farSites }) < 0) {
      const w = Math.floor(hash(q, r, seed + 6) * 6);
      const hexes = [[q, r], add([q, r], DIRS[(w + 1) % 6]), add([q, r], DIRS[(w + 5) % 6])];
      const clear = hexes.every(([a, b]) => isLandHex(a, b, seed) && siteAt(a, b, { farSites }) < 0 && !isTown(a, b))
        && [...farSites.map((h) => h[0]), [3, 0], [0, 3], [-3, 0], [0, -3], [3, -3], [-3, 3]]
          .every(([a, b]) => hexDistance(a - q, b - r) >= SITE_SPACING);
      if (clear) {
        const [region, ...rest] = farDeck;
        regionSites[NEAR_SITES + farSites.length] = region;
        farSites = [...farSites, hexes];
        farDeck = rest;
        changed = true;
        continue;
      }
    }
    // A find: something worth walking to, on workable land.
    const k = key(q, r);
    if (d >= 3 && hash(q, r, seed + 77) < FIND_CHANCE && isLandHex(q, r, seed)
      && siteAt(q, r, { farSites }) < 0 && !taken.has(k) && !finds.some((f) => f.q === q && f.r === r)) {
      const kind = FIND_ORDER_KEYS[Math.floor(hash(q, r, seed + 91) * FIND_ORDER_KEYS.length) % FIND_ORDER_KEYS.length];
      finds = [...finds, { id: `f${q}_${r}`, kind, q, r }];
      changed = true;
    }
  }
  return changed ? { ...civ, farSites, farDeck, regionSites, finds } : civ;
};

/**
 * Where a camp may appear: the band's land first (out in the fog, at the
 * edge of the known), then wild ground already seen, never the town or a
 * site, at least four out. In a fixed order so a spawn without dice is
 * still deterministic.
 */
export const campSpots = (civ) => {
  const seed = seedOf(civ);
  const ok = ([q, r]) => hexDistance(q, r) >= 4 && isLandHex(q, r, seed) && siteAt(q, r, civ) < 0 && !isTown(q, r);
  const order = (a, b) => hexDistance(...a) - hexDistance(...b) || hash(a[0], a[1], seed + 13) - hash(b[0], b[1], seed + 13);
  const band = bandOf(civ).filter(ok).sort(order);
  const seen = (civ.seen || []).map(unkey).filter(ok).sort(order);
  return [...band, ...seen];
};
